import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Add as AddIcon, Category as CategoryIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import PageHeader from '../../../components/common/PageHeader';
import SearchHeader from '../../../components/common/SearchHeader';
import PaginationComponent from '../../../components/common/PaginationComponent';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import QuoteTable from '../components/QuoteTable';
import QuoteFormDrawer from '../components/QuoteFormDrawer';
import QuotePreviewDrawer from '../components/QuotePreviewDrawer';
import quoteService from '../services/quoteService';
import clientService from '../../clients/services/clientService';
import projectService from '../../projects/services/projectService';

const ITEMS_PER_PAGE = 25;

const QuoteListingPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuotes, setTotalQuotes] = useState(0);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [clientLookup, setClientLookup] = useState({});
  const [projectLookup, setProjectLookup] = useState({});
  const [allClients, setAllClients] = useState([]); // For filter dropdown

  // Use refs to track lookup maps without causing re-renders
  const clientLookupRef = useRef({});
  const projectLookupRef = useRef({});

  // Update refs when state changes
  useEffect(() => {
    clientLookupRef.current = clientLookup;
  }, [clientLookup]);

  useEffect(() => {
    projectLookupRef.current = projectLookup;
  }, [projectLookup]);

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  // Load all clients for filter dropdown
  const loadAllClients = useCallback(async () => {
    try {
      const response = await clientService.getAll({ limit: 500 });
      if (response.success) {
        const clients = response.data || [];
        setAllClients(clients);
        // Also populate clientLookup for display
        const clientMap = {};
        clients.forEach((client) => {
          clientMap[client.id] = client.name || client.business_name;
        });
        setClientLookup((prev) => ({ ...prev, ...clientMap }));
      }
    } catch (err) {
      console.error('Error loading all clients:', err);
    }
  }, []);

  const loadClientsForQuotes = useCallback(async (quoteList) => {
    // Since loadAllClients already loads all clients, we only need to handle:
    // 1. Custom clients (quotes without client_id but with company_info)
    // 2. Clients that might not be in allClients (edge case)
    const newClientMap = {};
    const missingClientIds = [];
    
    quoteList.forEach((quote) => {
      // Handle quotes with client_id - check if not in lookup
      if (quote.client_id && !clientLookupRef.current[quote.client_id]) {
        // Only load if not in allClients (shouldn't happen, but safety check)
        missingClientIds.push(quote.client_id);
      }
      // Handle quotes without client_id (custom clients from company_info)
      if (!quote.client_id && quote.company_info?.full_name) {
        const customKey = `custom_${quote.id}`;
        if (!clientLookupRef.current[customKey]) {
          newClientMap[customKey] = quote.company_info.full_name;
        }
      }
    });

    // Load missing clients (fallback for edge cases)
    if (missingClientIds.length > 0) {
      const uniqueIds = Array.from(new Set(missingClientIds));
      const clientPromises = uniqueIds.map(async (clientId) => {
        try {
          const response = await clientService.getById(clientId);
          if (response.success && response.data) {
            return {
              id: clientId,
              name: response.data.name || response.data.business_name,
            };
          }
        } catch (err) {
          console.error(`Error loading client ${clientId}:`, err);
        }
        return null;
      });

      const loadedClients = (await Promise.all(clientPromises)).filter(Boolean);
      loadedClients.forEach((client) => {
        newClientMap[client.id] = client.name;
      });
    }
    
    // Update lookup only if there are new entries
    if (Object.keys(newClientMap).length > 0) {
      setClientLookup((prev) => ({ ...prev, ...newClientMap }));
    }
  }, []); // No dependencies - uses refs

  const loadProjectsForQuotes = useCallback(async (quoteList) => {
    const missingProjectIds = quoteList
      .map((quote) => quote.project_id)
      .filter((projectId) => projectId && !projectLookupRef.current[projectId]);

    if (missingProjectIds.length === 0) return;

    const uniqueIds = Array.from(new Set(missingProjectIds));
    const newProjectMap = {};

    const projectPromises = uniqueIds.map(async (projectId) => {
      try {
        const response = await projectService.getById(projectId);
        if (response.success && response.data) {
          return {
            id: projectId,
            name: response.data.name,
          };
        }
      } catch (err) {
        console.error(`Error loading project ${projectId}:`, err);
      }
      return null;
    });

    const loadedProjects = (await Promise.all(projectPromises)).filter(Boolean);
    loadedProjects.forEach((project) => {
      newProjectMap[project.id] = project.name;
    });

    // Update lookup only if there are new entries
    if (Object.keys(newProjectMap).length > 0) {
      setProjectLookup((prev) => ({ ...prev, ...newProjectMap }));
    }
  }, []); // No dependencies - uses refs

  const loadQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        skip: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (clientFilter) {
        // Convert clientFilter to number if it's a valid client ID
        const clientId = parseInt(clientFilter, 10);
        if (!isNaN(clientId)) {
          params.client_id = clientId;
        }
      }

      const response = await quoteService.getAll(params);
      if (response.success) {
        setQuotes(response.data || []);
        setTotalQuotes(response.total || 0);
        await loadClientsForQuotes(response.data || []);
        await loadProjectsForQuotes(response.data || []);
      } else {
        setQuotes([]);
        setTotalQuotes(0);
        setError(response.error || 'Σφάλμα φόρτωσης προσφορών');
      }
    } catch (err) {
      setQuotes([]);
      setTotalQuotes(0);
      setError('Σφάλμα φόρτωσης προσφορών');
    } finally {
      setLoading(false);
    }
  }, [clientFilter, currentPage, loadClientsForQuotes, loadProjectsForQuotes, statusFilter]);

  // Load all clients on mount for filter dropdown
  useEffect(() => {
    loadAllClients();
  }, [loadAllClients]);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, clientFilter]);

  const filteredQuotes = useMemo(() => {
    if (!searchTerm) return quotes;
    const lowerSearch = searchTerm.toLowerCase();
    return quotes.filter((quote) => {
      return (
        quote.quote_number?.toLowerCase().includes(lowerSearch) ||
        quote.title?.toLowerCase().includes(lowerSearch) ||
        (clientLookup?.[quote.client_id]?.toLowerCase().includes(lowerSearch))
      );
    });
  }, [quotes, searchTerm, clientLookup]);

  const handleCreateSuccess = () => {
    setNotification({ open: true, message: 'Η προσφορά δημιουργήθηκε επιτυχώς', severity: 'success' });
    loadQuotes();
  };

  const handleViewQuote = async (quote) => {
    const response = await quoteService.getById(quote.id);
    if (response.success) {
      const detailedQuote = response.data;
      setSelectedQuote(detailedQuote);
      setIsPreviewOpen(true);

      // Load client/project if not already in lookup (use refs to avoid stale closure)
      if (detailedQuote.client_id && !clientLookupRef.current[detailedQuote.client_id]) {
        const clientResponse = await clientService.getById(detailedQuote.client_id);
        if (clientResponse.success) {
          setClientLookup((prev) => ({ ...prev, [detailedQuote.client_id]: clientResponse.data.name || clientResponse.data.business_name }));
        }
      }
      if (detailedQuote.project_id && !projectLookupRef.current[detailedQuote.project_id]) {
        const projectResponse = await projectService.getById(detailedQuote.project_id);
        if (projectResponse.success) {
          setProjectLookup((prev) => ({ ...prev, [detailedQuote.project_id]: projectResponse.data.name }));
        }
      }
    } else {
      setNotification({ open: true, message: response.error || 'Σφάλμα φόρτωσης προσφοράς', severity: 'error' });
    }
  };

  const extractDateFromQuoteNumber = (quoteNumber) => {
    // Extract date from quote number in format Q-YYYY-MM-DD or Q-YYYY-MM-DD-NNN
    // Returns date in DD-MM-YYYY format, or null if not found
    try {
      if (!quoteNumber || !quoteNumber.startsWith('Q-')) {
        return null;
      }
      const parts = quoteNumber.substring(2).split('-'); // Remove 'Q-' and split by '-'
      if (parts.length >= 3) {
        // Format: YYYY-MM-DD or YYYY-MM-DD-NNN
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          // Format: DD-MM-YYYY
          return `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
        }
      }
    } catch (err) {
      console.error('Error extracting date from quote number:', err);
    }
    return null;
  };

  const handleDownloadQuote = async (quote) => {
    try {
      const response = await quoteService.downloadPDF(quote.id);
      if (response.success) {
        const { url } = response.data;
        const link = document.createElement('a');
        link.href = url;
        
        // Extract date from quote_number (format: Q-YYYY-MM-DD or Q-YYYY-MM-DD-NNN)
        let quoteDate = extractDateFromQuoteNumber(quote.quote_number);
        
        // Fallback to created_at if quote_number doesn't have date format
        if (!quoteDate && quote.created_at) {
          const date = new Date(quote.created_at);
          quoteDate = date.toLocaleDateString('el-GR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          }).replace(/\//g, '-');
        }
        
        // Final fallback to current date
        if (!quoteDate) {
          quoteDate = new Date().toLocaleDateString('el-GR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          }).replace(/\//g, '-');
        }
        
        link.download = `netgen_quote_${quoteDate}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Revoke blob URL after download to prevent memory leak
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);
        setNotification({ open: true, message: 'Το PDF δημιουργήθηκε επιτυχώς', severity: 'success' });
      } else {
        setNotification({ open: true, message: response.error || 'Σφάλμα δημιουργίας PDF', severity: 'error' });
      }
    } catch (err) {
      console.error('Error downloading quote PDF:', err);
      setNotification({ open: true, message: 'Σφάλμα δημιουργίας PDF', severity: 'error' });
    }
  };

  const handleCopyLink = async (quote) => {
    try {
      // Use public token for secure access
      if (!quote.public_token) {
        setNotification({ open: true, message: 'Η προσφορά δεν έχει public token', severity: 'error' });
        return;
      }
      const link = `${window.location.origin}/api/quotes/public/${quote.public_token}/preview`;
      await navigator.clipboard.writeText(link);
      setNotification({ open: true, message: 'Ο σύνδεσμος preview αντιγράφηκε', severity: 'info' });
    } catch (err) {
      setNotification({ open: true, message: 'Αδυναμία αντιγραφής συνδέσμου', severity: 'error' });
    }
  };

  const handleEditQuote = async (quote) => {
    const response = await quoteService.getById(quote.id);
    if (response.success) {
      const detailedQuote = response.data;
      setSelectedQuote(detailedQuote);
      setIsEditOpen(true);
      
      // Load client/project if not already in lookup (use refs to avoid stale closure)
      if (detailedQuote.client_id && !clientLookupRef.current[detailedQuote.client_id]) {
        const clientResponse = await clientService.getById(detailedQuote.client_id);
        if (clientResponse.success) {
          setClientLookup((prev) => ({ ...prev, [detailedQuote.client_id]: clientResponse.data.name || clientResponse.data.business_name }));
        }
      }
      if (detailedQuote.project_id && !projectLookupRef.current[detailedQuote.project_id]) {
        const projectResponse = await projectService.getById(detailedQuote.project_id);
        if (projectResponse.success) {
          setProjectLookup((prev) => ({ ...prev, [detailedQuote.project_id]: projectResponse.data.name }));
        }
      }
    } else {
      setNotification({ open: true, message: response.error || 'Σφάλμα φόρτωσης προσφοράς', severity: 'error' });
    }
  };

  const handleDeleteQuote = (quote) => {
    setSelectedQuote(quote);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedQuote) return;
    const response = await quoteService.delete(selectedQuote.id);
    if (response.success) {
      setNotification({ open: true, message: response.message, severity: 'success' });
      loadQuotes();
    } else {
      setNotification({ open: true, message: response.error, severity: 'error' });
    }
    setDeleteDialogOpen(false);
    setSelectedQuote(null);
  };

  const handleEditSuccess = () => {
    setNotification({ open: true, message: 'Η προσφορά ενημερώθηκε επιτυχώς', severity: 'success' });
    setIsEditOpen(false);
    setSelectedQuote(null);
    loadQuotes();
  };


  const pageActions = [
    <IconButton
      key="create"
      color="primary"
      onClick={() => setIsCreateOpen(true)}
      sx={{
        backgroundColor: 'primary.main',
        color: 'white',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
    >
      <AddIcon />
    </IconButton>,
    <IconButton
      key="packages"
      color="primary"
      onClick={() => navigate('/quote-packages')}
      sx={{
        backgroundColor: 'secondary.main',
        color: 'white',
        '&:hover': {
          backgroundColor: 'secondary.dark',
        },
      }}
    >
      <CategoryIcon />
    </IconButton>,
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Προσφορές"
        subtitle="Παρακολούθηση, δημιουργία και διαχείριση προσφορών"
        icon="🧾"
        actions={pageActions}
      />

      <SearchHeader
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Αναζήτηση προσφορών (αριθμός, τίτλος, πελάτης)"
      />

      <Box sx={{
        mb: 3,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'center',
      }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="quote-status-filter-label">Κατάσταση</InputLabel>
          <Select
            labelId="quote-status-filter-label"
            label="Κατάσταση"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="all">Όλες</MenuItem>
            <MenuItem value="draft">Πρόχειρες</MenuItem>
            <MenuItem value="sent">Απεσταλμένες</MenuItem>
            <MenuItem value="accepted">Εγκεκριμένες</MenuItem>
            <MenuItem value="rejected">Απορριφθείσες</MenuItem>
            <MenuItem value="expired">Ληγμένες</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="client-filter-label">Πελάτης</InputLabel>
          <Select
            labelId="client-filter-label"
            label="Πελάτης"
            value={clientFilter}
            onChange={(event) => setClientFilter(event.target.value)}
          >
            <MenuItem value="">
              <em>Όλοι οι πελάτες</em>
            </MenuItem>
            {allClients.map((client) => (
              <MenuItem key={client.id} value={client.id}>
                {client.name || client.business_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Box>
      )}

      <QuoteTable
        data={filteredQuotes}
        loading={loading}
        sortField="quote_number"
        sortDirection="desc"
        onSort={() => {}}
        onViewQuote={handleViewQuote}
        onDownloadQuote={handleDownloadQuote}
        onCopyLink={handleCopyLink}
        onEditQuote={handleEditQuote}
        onDeleteQuote={handleDeleteQuote}
        clientLookup={clientLookup}
        projectLookup={projectLookup}
      />

      <PaginationComponent
        currentPage={currentPage}
        totalPages={Math.ceil(totalQuotes / ITEMS_PER_PAGE)}
        totalItems={totalQuotes}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={() => {}}
        disableItemsPerPage
      />

      <QuoteFormDrawer
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreateSuccess}
      />

      <QuoteFormDrawer
        open={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedQuote(null);
        }}
        onSubmit={handleEditSuccess}
        quote={selectedQuote}
      />

      <QuotePreviewDrawer
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        quoteId={selectedQuote?.id}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedQuote(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Διαγραφή Προσφοράς"
        message={
          <Typography>
            Θέλεις σίγουρα να διαγράψεις την προσφορά{' '}
            <strong>{selectedQuote?.quote_number}</strong>; Η ενέργεια είναι μη αναστρέψιμη.
          </Typography>
        }
        confirmText="Διαγραφή"
        cancelText="Ακύρωση"
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuoteListingPage;

